import {
  CATALOG_ITEM_STATUSES,
  PROPOSAL_STATUSES,
  REVIEW_GROUP_STATUSES,
  REVIEW_SUBJECTS,
  RUN_STATUSES,
} from '@services/fmdc';

// Each list's filters as a query string, and back — one module so the two directions cannot
// drift (the listFilterUrl.js rule). The URL IS each list's filter state: a reload, a shared link
// and browser-back all land on the same view. Unknown values fall back to the default for that key
// rather than emptying the list; unknown keys are ignored.

const oneOf = (value, allowed, fallback) => (value && allowed.has(value) ? value : fallback);

const nonNegativeInt = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : fallback;
};

const isoDateOf = (value) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null);

// A tiny parse/serialize factory: `fields` is `{ key: { param, default, read(raw, defaults) } }`.
// `read` returns the typed value; `serialize` writes only what differs from the default, in the
// fields' declared order, so the same view always produces the same URL and the default view
// produces none.
const makeListUrl = (fields) => {
  const keys = Object.keys(fields);
  const defaults = Object.fromEntries(keys.map((key) => [key, fields[key].default]));

  const parse = (search) => {
    const params = new URLSearchParams(search || '');
    const result = {};
    keys.forEach((key) => {
      const field = fields[key];
      result[key] = field.read(params.get(field.param), defaults[key]);
    });
    return result;
  };

  const serialize = (filters) => {
    const params = new URLSearchParams();
    keys.forEach((key) => {
      const field = fields[key];
      const value = filters[key];
      if (value === undefined || value === null || value === '' || value === defaults[key]) return;
      params.set(field.param, String(value));
    });
    return params;
  };

  return { parse, serialize, defaults };
};

const text = (param, fallback = '') => ({ param, default: fallback, read: (raw, d) => raw || d });
const offset = { param: 'offset', default: 0, read: (raw, d) => nonNegativeInt(raw, d) };
const enumOf = (param, values, fallback) => ({
  param,
  default: fallback,
  read: (raw, d) => oneOf(raw, new Set(values), d),
});

// Runs list: status (All or one), issuer id, offset.
export const RUN_STATUS_FILTERS = {
  ALL: 'all',
  RUNNING: 'running',
  REVIEW: RUN_STATUSES.REVIEW,
  LOADABLE: RUN_STATUSES.LOADABLE,
  ERROR: RUN_STATUSES.ERROR,
};

export const runsUrl = makeListUrl({
  status: enumOf('status', Object.values(RUN_STATUS_FILTERS), RUN_STATUS_FILTERS.ALL),
  issuer: text('issuer'),
  offset,
});

// Review queue: subject, status, reason, sheet, offset. `reason` and `sheet` are free text here
// because their vocabularies come from the run, not from a constant.
export const QUEUE_SUBJECTS = { ...REVIEW_SUBJECTS, BOTH: 'both' };

export const queueUrl = makeListUrl({
  subject: enumOf('subject', Object.values(QUEUE_SUBJECTS), QUEUE_SUBJECTS.BOTH),
  status: enumOf('status', Object.values(REVIEW_GROUP_STATUSES), REVIEW_GROUP_STATUSES.OPEN),
  reason: text('reason'),
  sheet: text('sheet'),
  offset,
});

// Run detail tab.
export const RUN_TABS = { OVERVIEW: 'overview', VALIDATION: 'validation', STATIC_INFO: 'static_info' };

export const runDetailUrl = makeListUrl({
  tab: enumOf('tab', Object.values(RUN_TABS), RUN_TABS.OVERVIEW),
});

// Catalog: tab (items | proposals), search, section, status, offset.
export const CATALOG_TABS = { ITEMS: 'items', PROPOSALS: 'proposals' };
export const CATALOG_STATUS_FILTERS = { ALL: 'all', ...CATALOG_ITEM_STATUSES };
export const PROPOSAL_STATUS_FILTERS = { ALL: 'all', ...PROPOSAL_STATUSES };

export const catalogUrl = makeListUrl({
  tab: enumOf('tab', Object.values(CATALOG_TABS), CATALOG_TABS.ITEMS),
  q: text('q'),
  section: text('section'),
  status: enumOf('status', Object.values(CATALOG_STATUS_FILTERS), CATALOG_STATUS_FILTERS.ACTIVE),
  proposalStatus: enumOf('proposals', Object.values(PROPOSAL_STATUS_FILTERS), PROPOSAL_STATUS_FILTERS.PENDING),
  offset,
});

// Audit: run id, event type, actor type, from / to, offset.
export const ACTOR_TYPE_FILTERS = { ALL: 'all', USER: 'user', ADMIN_USER: 'admin_user', SYSTEM: 'system' };

export const auditUrl = makeListUrl({
  runId: text('run_id'),
  eventType: text('event_type'),
  actorType: enumOf('actor_type', Object.values(ACTOR_TYPE_FILTERS), ACTOR_TYPE_FILTERS.ALL),
  from: { param: 'from', default: null, read: (raw) => isoDateOf(raw) },
  to: { param: 'to', default: null, read: (raw) => isoDateOf(raw) },
  offset,
});

// Path builders, so no view spells another view's segments.
export const fmdcPaths = {
  runs: (base) => base,
  run: (base, runId) => `${base}/runs/${runId}`,
  review: (base, runId) => `${base}/runs/${runId}/review`,
  value: (base, runId, rowId) => `${base}/runs/${runId}/values/${rowId}`,
  unmapped: (base, runId, unmappedId) => `${base}/runs/${runId}/unmapped/${unmappedId}`,
  catalog: (base) => `${base}/catalog`,
  audit: (base) => `${base}/audit`,
  settings: (base) => `${base}/settings`,
};
